// Formas de pago de un cobro, hasta 3: medio, monto y comprobante opcional por renglón, y el cuadre.
// La comparten el cobro de contado, el enganche y el carrito de accesorios.
import { useEffect, useId, useRef } from 'react';
import { Plus, X } from 'lucide-react';
import { Button, Input, Select } from '@/kit';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/cn';
import { formatMoneyCents } from '@/shared/lib/format/money';
import { PAYMENT_LINE_LABEL } from '@/shared/lib/format/salePaymentLines';
import { AmountInput } from './AmountInput';
import {
    MAX_PAYMENTS,
    addPaymentDraft,
    isMethodTaken,
    paymentAmountCents,
    paymentsBalance,
    receiptErrors,
    removePaymentDraft,
    type PaymentDraft,
    type PaymentsBalance,
} from '../salePayments';
import type { PaymentMethod } from '../types';

const LABEL_CAPS = 'block text-[10px] uppercase tracking-[0.06em] text-muted-foreground';
const HINT = 'text-[11px] leading-[1.35] text-muted-foreground';

const METHODS: readonly PaymentMethod[] = ['CASH', 'CARD', 'SPEI'];

type Layout = 'row' | 'stack';

export interface PaymentListFormProps {
    drafts: readonly PaymentDraft[];
    onChange: (next: readonly PaymentDraft[]) => void;
    totalCents: number;
    title?: string;
    /** `stack` para paneles angostos como el carrito. */
    layout?: Layout;
}

export function PaymentListForm({
    drafts,
    onChange,
    totalCents,
    title = '¿Cómo va a pagar?',
    layout = 'row',
}: PaymentListFormProps) {
    const baseId = useId();
    const addRef = useRef<HTMLButtonElement>(null);
    /** Renglón que recibe el foco tras quitar otro; `null` = el botón de agregar. */
    const pendingFocus = useRef<string | null | undefined>(undefined);
    const remaining = MAX_PAYMENTS - drafts.length;
    const rowId = (draftId: string): string => `${baseId}-${draftId}`;
    const update = (id: string, patch: Partial<PaymentDraft>): void => {
        onChange(drafts.map((d) => (d.id === id ? { ...d, ...patch } : d)));
    };

    // El foco se mueve después de que el renglón quitado ya salió del DOM.
    useEffect(() => {
        const target = pendingFocus.current;
        if (target === undefined) return;
        pendingFocus.current = undefined;
        const el = target === null ? addRef.current : document.getElementById(`${target}-method`);
        el?.focus();
    }, [drafts]);

    return (
        <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className={LABEL_CAPS}>{title}</span>
                <span className={HINT}>Hasta 3 formas · la tarjeta se puede repetir</span>
            </div>

            {drafts.map((draft, index) => (
                <PaymentRow
                    key={draft.id}
                    id={rowId(draft.id)}
                    index={index}
                    draft={draft}
                    drafts={drafts}
                    totalCents={totalCents}
                    layout={layout}
                    onPatch={(patch) => {
                        update(draft.id, patch);
                    }}
                    onRemove={() => {
                        const previous = drafts[index - 1] as PaymentDraft | undefined;
                        pendingFocus.current = previous ? rowId(previous.id) : null;
                        onChange(removePaymentDraft(drafts, draft.id));
                    }}
                />
            ))}

            <div className="flex flex-col gap-1">
                <Button
                    ref={addRef}
                    variant="outline"
                    disabled={remaining === 0}
                    leftIcon={<Plus aria-hidden />}
                    onClick={() => {
                        onChange(addPaymentDraft(drafts, totalCents, crypto.randomUUID()));
                    }}
                    className="h-10 rounded-[10px] border-[1.5px] border-dashed font-semibold"
                >
                    Agregar otra forma de pago
                    {remaining > 0 && (
                        <span className="font-normal text-muted-foreground">
                            · {remaining === 1 ? 'queda 1' : `quedan ${String(remaining)}`}
                        </span>
                    )}
                </Button>
                {remaining === 0 && (
                    <span className={HINT}>Llegaste al máximo de 3 formas de pago.</span>
                )}
            </div>

            {drafts.length > 1 && (
                <BalanceBar balance={paymentsBalance(drafts, totalCents)} totalCents={totalCents} />
            )}
        </div>
    );
}

function PaymentRow({
    id,
    index,
    draft,
    drafts,
    totalCents,
    layout,
    onPatch,
    onRemove,
}: {
    id: string;
    index: number;
    draft: PaymentDraft;
    drafts: readonly PaymentDraft[];
    totalCents: number;
    layout: Layout;
    onPatch: (patch: Partial<PaymentDraft>) => void;
    onRemove: () => void;
}) {
    const single = drafts.length === 1;
    const amountCents = paymentAmountCents(drafts, draft, totalCents);
    const number = String(index + 1);
    const options = METHODS.map((method) => {
        const taken = isMethodTaken(drafts, method, draft.id);
        return {
            value: method,
            label: PAYMENT_LINE_LABEL[method],
            disabled: taken,
            hint: taken
                ? 'ya está en el cobro'
                : method === 'CARD'
                  ? 'se puede repetir'
                  : undefined,
        };
    });

    return (
        <div
            role="group"
            aria-label={`Forma de pago ${number}`}
            className="rounded-lg border border-border bg-card"
        >
            <div
                className={cn(
                    'flex flex-wrap items-end gap-3 p-3',
                    layout === 'row' && 'md:flex-nowrap',
                )}
            >
                <span
                    aria-hidden
                    className="mb-1.5 flex size-5 flex-none items-center justify-center rounded-full bg-muted text-[11px] font-semibold"
                >
                    {number}
                </span>
                <div className="min-w-[160px] flex-1">
                    <label htmlFor={`${id}-method`} className={cn(LABEL_CAPS, 'mb-1.5')}>
                        Forma de pago {number}
                    </label>
                    <Select
                        id={`${id}-method`}
                        value={draft.method}
                        options={options}
                        onChange={(value) => {
                            onPatch({ method: value as PaymentMethod });
                        }}
                        className="h-8 text-[12.5px]"
                    />
                </div>
                <div className="w-[150px]">
                    <label htmlFor={`${id}-amount`} className={cn(LABEL_CAPS, 'mb-1.5')}>
                        Monto
                    </label>
                    <div className="flex items-center gap-1">
                        <span className="text-[12.5px] text-muted-foreground">$</span>
                        <AmountInput
                            id={`${id}-amount`}
                            value={amountCents / 100}
                            disabled={single}
                            onValueChange={(pesos) => {
                                onPatch({ amountCents: Math.round(pesos * 100) });
                            }}
                            className="h-8 w-full text-right text-[12.5px] font-semibold"
                        />
                    </div>
                </div>
                {!single && (
                    <Button
                        variant="ghost"
                        size="icon"
                        aria-label={`Quitar forma de pago ${number}`}
                        onClick={onRemove}
                        className="size-8 flex-none text-muted-foreground"
                    >
                        <X aria-hidden />
                    </Button>
                )}
            </div>
            <div className="border-t border-border px-3 py-3">
                <PaymentReceipt
                    id={id}
                    draft={draft}
                    amountCents={amountCents}
                    compact={layout === 'stack'}
                    onPatch={onPatch}
                />
            </div>
        </div>
    );
}

const RECEIPT_INPUT = 'h-8 text-[12.5px] font-mono';
const RECEIPT_LABEL = cn(LABEL_CAPS, 'whitespace-nowrap');

/** Deja solo dígitos o solo letras y números, con tope de largo. */
function sanitize(value: string, allowed: RegExp, maxLength: number): string {
    return value.replace(allowed, '').slice(0, maxLength);
}

function PaymentReceipt({
    id,
    draft,
    amountCents,
    compact,
    onPatch,
}: {
    id: string;
    draft: PaymentDraft;
    amountCents: number;
    /** Panel angosto: etiquetas cortas para que no se partan. */
    compact: boolean;
    onPatch: (patch: Partial<PaymentDraft>) => void;
}) {
    const errors = receiptErrors(draft);
    const optional = (full: string, short = full): string =>
        compact ? `${short} (opc.)` : `${full} (opcional)`;

    if (draft.method === 'CASH') {
        return (
            <div className="flex items-start gap-2">
                <Checkbox
                    id={`${id}-cash-ok`}
                    checked={draft.cashOk}
                    onCheckedChange={(checked) => {
                        onPatch({ cashOk: checked === true });
                    }}
                    className="mt-0.5"
                />
                <label
                    htmlFor={`${id}-cash-ok`}
                    className="cursor-pointer text-[12.5px] font-medium"
                >
                    Confirmo que el cliente pagó{' '}
                    <span className="tabular-nums">{formatMoneyCents(amountCents)}</span> en la caja
                </label>
            </div>
        );
    }

    if (draft.method === 'CARD') {
        return (
            <div className="flex flex-col gap-2">
                <div className="flex flex-wrap items-start gap-3">
                    <div className="w-[130px]">
                        <Input
                            id={`${id}-card-last4`}
                            label={optional('Últimos 4 dígitos', 'Últimos 4')}
                            inputMode="numeric"
                            autoComplete="off"
                            maxLength={4}
                            placeholder="XXXX"
                            value={draft.cardLast4}
                            labelClassName={RECEIPT_LABEL}
                            error={errors.cardLast4}
                            onChange={(e) => {
                                onPatch({ cardLast4: sanitize(e.target.value, /\D/g, 4) });
                            }}
                            className={cn(
                                RECEIPT_INPUT,
                                'text-center font-semibold tracking-[0.2em]',
                            )}
                        />
                    </div>
                    <div className="w-[220px]">
                        <Input
                            id={`${id}-card-auth`}
                            label={optional('Autorización')}
                            autoComplete="off"
                            maxLength={64}
                            placeholder="123456"
                            value={draft.authCode}
                            labelClassName={RECEIPT_LABEL}
                            error={errors.authCode}
                            onChange={(e) => {
                                onPatch({ authCode: e.target.value.slice(0, 64) });
                            }}
                            className={RECEIPT_INPUT}
                        />
                    </div>
                </div>
                <p className={HINT}>
                    Vienen en el voucher de la terminal. Si no los tienes, puedes cobrar igual.
                </p>
            </div>
        );
    }

    return (
        <div className="flex flex-wrap items-start gap-3">
            <div className="w-[260px]">
                <Input
                    id={`${id}-spei-key`}
                    label={optional('Clave de rastreo')}
                    autoComplete="off"
                    maxLength={30}
                    placeholder="Hasta 30 letras o números"
                    value={draft.speiKey}
                    labelClassName={RECEIPT_LABEL}
                    error={errors.speiKey}
                    onChange={(e) => {
                        onPatch({ speiKey: sanitize(e.target.value, /[^A-Za-z0-9]/g, 30) });
                    }}
                    className={RECEIPT_INPUT}
                />
            </div>
            <div className="w-[220px]">
                <Input
                    id={`${id}-spei-clabe`}
                    label={optional('CLABE ordenante', 'CLABE')}
                    inputMode="numeric"
                    autoComplete="off"
                    maxLength={18}
                    placeholder="18 dígitos"
                    value={draft.senderClabe}
                    labelClassName={RECEIPT_LABEL}
                    error={errors.senderClabe}
                    onChange={(e) => {
                        onPatch({ senderClabe: sanitize(e.target.value, /\D/g, 18) });
                    }}
                    className={RECEIPT_INPUT}
                />
            </div>
        </div>
    );
}

const BALANCE_TONE: Record<PaymentsBalance['status'], string> = {
    MATCH: 'border-success/40 bg-success/10 text-success',
    SHORT: 'border-warning-soft-border bg-warning-soft text-warning',
    OVER: 'border-destructive/40 bg-destructive/10 text-destructive',
};

function BalanceBar({ balance, totalCents }: { balance: PaymentsBalance; totalCents: number }) {
    const label =
        balance.status === 'MATCH'
            ? 'Cuadra con el total'
            : balance.status === 'SHORT'
              ? `Faltan ${formatMoneyCents(balance.missingCents)}`
              : `Te pasaste por ${formatMoneyCents(-balance.missingCents)}`;
    return (
        <div
            role="status"
            className={cn(
                'flex flex-wrap items-center justify-between gap-2 rounded-lg border px-3 py-2.5',
                BALANCE_TONE[balance.status],
            )}
        >
            <span className="text-[12.5px] font-semibold">{label}</span>
            <span className="text-[12px] tabular-nums">
                {formatMoneyCents(balance.assignedCents)} de {formatMoneyCents(totalCents)}
            </span>
        </div>
    );
}
